import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, describeApiError } from "@/lib/api";
import { CircleNotch, Plant, Cow, Barn, MapPinLine } from "@phosphor-icons/react";

const money = (n) => `$${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function MetricCard({ title, value, subtitle, testId, icon: Icon }) {
  return (
    <div className="bk-card p-5" data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={18} weight="duotone" color="var(--bk-accent)" />
        <p className="text-xs uppercase tracking-[0.16em] font-semibold" style={{ color: "var(--bk-text-2)" }}>{title}</p>
      </div>
      <p className="font-display text-3xl font-bold" style={{ color: "var(--bk-text)" }}>{value}</p>
      <p className="text-xs mt-2" style={{ color: "var(--bk-text-2)" }}>{subtitle}</p>
    </div>
  );
}

function CategoryPicker({ title, selected, options, onToggle, testIdPrefix }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-[0.16em] font-semibold mb-2" style={{ color: "var(--bk-text-2)" }}>{title}</p>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => {
          const active = selected.includes(opt);
          return (
            <button
              key={opt}
              type="button"
              onClick={() => onToggle(opt)}
              data-testid={`${testIdPrefix}-${opt.replace(/\s+/g, "-").toLowerCase()}`}
              className="px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors"
              style={active ? { borderColor: "var(--bk-accent)", background: "var(--bk-rowan-bg)", color: "var(--bk-accent)" } : { borderColor: "var(--bk-border)", color: "var(--bk-text)" }}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function AgricultureProduction({ businessId, business, onBusinessChange }) {
  const [payload, setPayload] = useState(null);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ head_count: "", acreage: "", unit_count: "", unit_label: "unit", locations: "", head_categories: [], acre_categories: [], unit_categories: [] });

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, { data: categoryData }] = await Promise.all([
        api.get(`/businesses/${businessId}/agriculture/production`),
        api.get(`/businesses/${businessId}/categories`),
      ]);
      setPayload(data);
      setCats(categoryData.expense || []);
      setForm({
        head_count: String(data.settings.head_count || ""),
        acreage: String(data.settings.acreage || ""),
        unit_count: String(data.settings.unit_count || ""),
        unit_label: data.settings.unit_label || "unit",
        locations: (data.settings.locations || []).join(", "),
        head_categories: data.settings.head_categories || [],
        acre_categories: data.settings.acre_categories || [],
        unit_categories: data.settings.unit_categories || [],
      });
      onBusinessChange?.({ ...business, agriculture_settings: data.settings });
    } catch (err) {
      toast.error(describeApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [businessId]);

  const toggleCategory = (bucket, opt) => {
    setForm((f) => ({
      ...f,
      [bucket]: f[bucket].includes(opt) ? f[bucket].filter((x) => x !== opt) : [...f[bucket], opt],
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/businesses/${businessId}/agriculture/production`, {
        head_count: Number(form.head_count) || 0,
        acreage: Number(form.acreage) || 0,
        unit_count: Number(form.unit_count) || 0,
        unit_label: form.unit_label,
        locations: form.locations.split(",").map((x) => x.trim()).filter(Boolean),
        head_categories: form.head_categories,
        acre_categories: form.acre_categories,
        unit_categories: form.unit_categories,
      });
      setPayload(data);
      onBusinessChange?.({ ...business, agriculture_settings: data.settings });
      toast.success("Production settings saved");
    } catch (err) {
      toast.error(describeApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const locationLeader = useMemo(() => payload?.locations?.[0], [payload]);

  if (loading) {
    return <div className="bk-card p-8 flex items-center justify-center gap-2 text-sm" data-testid="agriculture-production-loading"><CircleNotch size={18} className="animate-spin" /> Loading production costs…</div>;
  }

  return (
    <div className="space-y-6" data-testid="agriculture-production-tab">
      <div className="bk-rowan-card p-6">
        <p className="text-xs uppercase tracking-[0.18em] font-semibold" style={{ color: "var(--bk-accent)" }}>Production Costs</p>
        <h3 className="font-display text-2xl font-bold mt-2" style={{ color: "var(--bk-text)" }}>Know your true cost per head, acre, and unit</h3>
        <p className="text-sm mt-2" style={{ color: "var(--bk-text-2)" }} data-testid="agriculture-rowan-primary">{payload?.summary?.rowan_primary}</p>
      </div>

      <div className="bk-card p-6 space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <input value={form.head_count} onChange={(e) => setForm((f) => ({ ...f, head_count: e.target.value }))} data-testid="ag-head-count" type="number" min="0" placeholder="Number of head" className="rounded-md border px-3 py-2.5 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
          <input value={form.acreage} onChange={(e) => setForm((f) => ({ ...f, acreage: e.target.value }))} data-testid="ag-acreage" type="number" min="0" placeholder="Acreage" className="rounded-md border px-3 py-2.5 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
          <input value={form.unit_count} onChange={(e) => setForm((f) => ({ ...f, unit_count: e.target.value }))} data-testid="ag-unit-count" type="number" min="0" placeholder="Units produced / sold" className="rounded-md border px-3 py-2.5 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
          <input value={form.unit_label} onChange={(e) => setForm((f) => ({ ...f, unit_label: e.target.value }))} data-testid="ag-unit-label" placeholder="bushels, bales, dozen eggs" className="rounded-md border px-3 py-2.5 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
        </div>
        <input value={form.locations} onChange={(e) => setForm((f) => ({ ...f, locations: e.target.value }))} data-testid="ag-location-list" placeholder="North field, South field, East ranch" className="w-full rounded-md border px-3 py-2.5 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
        <CategoryPicker title="Cost per head categories" selected={form.head_categories} options={cats} onToggle={(opt) => toggleCategory("head_categories", opt)} testIdPrefix="ag-head-cat" />
        <CategoryPicker title="Cost per acre categories" selected={form.acre_categories} options={cats} onToggle={(opt) => toggleCategory("acre_categories", opt)} testIdPrefix="ag-acre-cat" />
        <CategoryPicker title="Cost per unit categories" selected={form.unit_categories} options={cats} onToggle={(opt) => toggleCategory("unit_categories", opt)} testIdPrefix="ag-unit-cat" />
        <div className="flex justify-end">
          <button onClick={save} disabled={saving} data-testid="agriculture-save-settings" className="bk-btn-primary px-5 py-2.5 text-sm font-semibold disabled:opacity-60">
            {saving ? <span className="inline-flex items-center gap-2"><CircleNotch size={16} className="animate-spin" /> Saving…</span> : "Save production settings"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <MetricCard title="Cost per head" value={money(payload?.per_head?.cost_per || 0)} subtitle={`Profit per head ${money(payload?.per_head?.profit_per || 0)}`} testId="ag-cost-per-head" icon={Cow} />
        <MetricCard title="Cost per acre" value={money(payload?.per_acre?.cost_per || 0)} subtitle={`Profit per acre ${money(payload?.per_acre?.profit_per || 0)}`} testId="ag-cost-per-acre" icon={Plant} />
        <MetricCard title={`Cost per ${payload?.per_unit?.unit_label || payload?.settings?.unit_label || "unit"}`} value={money(payload?.per_unit?.cost_per || 0)} subtitle={`Profit per ${payload?.per_unit?.unit_label || "unit"} ${money(payload?.per_unit?.profit_per || 0)}`} testId="ag-cost-per-unit" icon={Barn} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_1.1fr] gap-6">
        <div className="bk-card p-6" data-testid="agriculture-rowan-lines-card">
          <h4 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>Rowan&apos;s agriculture read</h4>
          <ul className="space-y-3 mt-4 text-sm" style={{ color: "var(--bk-text)" }}>
            {(payload?.rowan_voice_lines || []).map((line) => <li key={line}>• {line}</li>)}
          </ul>
        </div>

        <div className="bk-card p-6" data-testid="agriculture-location-report">
          <div className="flex items-center gap-2 mb-2">
            <MapPinLine size={18} weight="duotone" color="var(--bk-success)" />
            <h4 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>Profitability by location</h4>
          </div>
          <p className="text-sm mb-4" style={{ color: "var(--bk-text-2)" }} data-testid="agriculture-location-rowan">{payload?.rowan_location_line || "Add a few tagged transactions and Rowan will compare your fields side by side."}</p>
          <div className="space-y-3">
            {(payload?.locations || []).map((row) => (
              <div key={row.location} className="rounded-2xl border p-4" style={{ borderColor: "var(--bk-border)" }} data-testid={`ag-location-row-${row.location.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="flex items-center justify-between gap-3">
                  <p className="font-semibold" style={{ color: "var(--bk-text)" }}>{row.location}</p>
                  <span className="text-sm font-bold" style={{ color: row.net_profit >= 0 ? "var(--bk-success)" : "#B23B1F" }}>{money(row.net_profit)}</span>
                </div>
                <p className="text-xs mt-2" style={{ color: "var(--bk-text-2)" }}>Income {money(row.income)} · Expenses {money(row.expense)} · Margin {row.margin_pct}%</p>
              </div>
            ))}
          </div>
          {locationLeader && <p className="text-xs mt-4" style={{ color: "var(--bk-text-2)" }}>Current leader: <strong style={{ color: "var(--bk-text)" }}>{locationLeader.location}</strong></p>}
        </div>
      </div>
    </div>
  );
}